import { useEffect, useState } from "react";
import { Save, AlertTriangle, FileWarning } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { LineasFactura } from "@/components/LineasFactura";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const desdeFactura = (f) => (f.detalle || []).map((d) => ({
  concepto: d.concepto, monto_unitario: String(d.monto_unitario), cantidad: d.cantidad || 1,
  articulo_id: d.articulo_id || null, combo_id: d.combo_id || null,
  categoria: d.categoria || null, venta_id: d.venta_id || null,
}));

/** Edición de una factura emitida: agregar o quitar combos, uniformes, libros o conceptos. */
export const EditarFactura = ({ factura, onClose, onGuardado }) => {
  const [lineas, setLineas] = useState([]);
  const [vencimiento, setVencimiento] = useState("");
  const [motivo, setMotivo] = useState("");
  const [articulos, setArticulos] = useState([]);
  const [combos, setCombos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!factura) return;
    setLineas(desdeFactura(factura));
    setVencimiento((factura.fecha_vencimiento || "").slice(0, 10));
    setMotivo("");
    api.get("/inventario").then((r) => setArticulos(r.data.articulos || []))
      .catch(() => setArticulos([]));
    api.get("/combos").then((r) => setCombos(r.data)).catch(() => setCombos([]));
  }, [factura]);

  if (!factura) return null;

  const pagado = Number(factura.monto_pagado || 0);
  const total = lineas.reduce(
    (s, l) => s + Number(l.cantidad || 0) * Number(l.monto_unitario || 0), 0);
  const reemplaza = pagado > 0 || ["pagado", "en_verificacion"].includes(factura.estado)
    || !!factura.enviada_correo_en;

  const guardar = async () => {
    if (lineas.some((l) => !l.concepto.trim() || Number(l.monto_unitario) <= 0)) {
      return toast.error("Cada línea necesita concepto y un precio mayor a cero");
    }
    if (total < pagado - 0.01) {
      return toast.error(`El total no puede quedar por debajo de lo ya pagado (${money(pagado)})`);
    }
    setGuardando(true);
    try {
      const { data } = await api.put(`/facturas/${factura.id}`, {
        detalle: lineas.map((l) => ({
          concepto: l.concepto.trim(), monto_unitario: Number(l.monto_unitario),
          cantidad: Number(l.cantidad) || 1, articulo_id: l.articulo_id,
          combo_id: l.combo_id, categoria: l.categoria, venta_id: l.venta_id,
        })),
        fecha_vencimiento: vencimiento ? new Date(vencimiento).toISOString() : null,
        motivo: motivo.trim() || null,
      });
      toast.success(data.reemplazada
        ? `Factura ${data.anulada} anulada y reemplazada por ${data.factura.ncf}`
        : `Factura ${data.factura.ncf} actualizada a ${money(data.factura.monto_total)}`);
      onGuardado();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
    setGuardando(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="modal-editar-factura">
        <DialogHeader>
          <DialogTitle className="font-display">Editar factura {factura.ncf}</DialogTitle>
          <DialogDescription>
            {factura.cliente_nombre || factura.tutor_nombre} ·{" "}
            {factura.estudiantes_nombres?.length > 1
              ? `${factura.estudiantes_nombres.length} estudiantes`
              : factura.estudiante_nombre} · pagado {money(pagado)}
          </DialogDescription>
        </DialogHeader>

        <div className={`flex items-start gap-3 rounded-md border p-4 text-xs ${
          reemplaza ? "border-destructive/40 bg-destructive/5" : "border-border bg-muted/30"}`}
             data-testid="aviso-ncf-edicion">
          {reemplaza ? <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                     : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
          <span>
            {reemplaza ? (
              <>
                <b>Esta factura ya tiene pagos o fue enviada al tutor.</b> Al guardar se anula{" "}
                {factura.ncf} y se emite una nueva con el siguiente comprobante, trasladando los
                pagos registrados ({money(pagado)}).
              </>
            ) : (
              <>
                La factura sigue pendiente y no se ha enviado: <b>conserva el mismo {factura.ncf}</b>{" "}
                y el cambio queda registrado en la bitácora del sistema.
              </>
            )}
          </span>
        </div>

        <LineasFactura lineas={lineas} onChange={setLineas} articulos={articulos} combos={combos} />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Vencimiento</Label>
            <Input type="date" value={vencimiento} data-testid="editar-vencimiento"
                   onChange={(e) => setVencimiento(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Motivo del cambio (queda en la bitácora)</Label>
            <Input value={motivo} placeholder="Se agregó el uniforme de educación física"
                   data-testid="editar-motivo" onChange={(e) => setMotivo(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-4">
          <span className="text-xs text-muted-foreground">
            Total anterior {money(factura.monto_total)}
            {factura.monto_descuento > 0 && ` · descuento ${money(factura.monto_descuento)}`}
          </span>
          <Badge variant="outline" className="font-mono text-sm" data-testid="nuevo-total-factura">
            Nuevo total: {money(total)}
          </Badge>
        </div>

        <Button className="w-full rounded-full py-6" onClick={guardar} disabled={guardando}
                data-testid="guardar-factura-editada">
          <Save className="mr-2 h-4 w-4" />
          {reemplaza ? "Anular y emitir la factura corregida" : "Guardar cambios de la factura"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
